import { yupResolver } from '@/gf/lib/yupResolver'
import { Controller, useForm, useWatch } from 'react-hook-form'
import * as Yup from 'yup'

import Action from '@/gf/components/Action'
import AddressInput from '@/gf/components/AddressInput'
import CloseModalButton from '@/gf/components/CloseModalButton'
import GoogleBusinessTypeahead from '@/gf/components/GoogleBusinessTypeahead'
import Modal from '@/gf/components/ModalNext'
import Field from '@/gf/components/next/forms/Field'
import TextInput from '@/gf/components/next/forms/TextInput'
import PhoneInput from '@/gf/components/PhoneInput'
import useMsgs from '@/gf/hooks/useMsgs'
import MutationErrorM from '@/gf/modules/MutationError'
import { useAddCustomerMutation } from '@/suppliers/_gen/gql'
import useGqlClient from '@/suppliers/hooks/useGqlClient'
import AddressModule from '@/suppliers/modules/Address'
import { CreateOrderFormCustomer, ModalSize } from '@/types'
import { CustomerForm } from './types'

const validationSchema = Yup.object().shape({
  accountNumber: Yup.string().required('Account Number is required'),
  companyName: Yup.string().required('Company Name is required'),
  companyPhoneNumber: Yup.string().required('Phone Number is required'),
  address: Yup.object().nullable(),
  contactName: Yup.string().required('Contact Name is required'),
  contactEmail: Yup.string().email('Must be a valid email address').required('Email is required'),
  contactPhoneNumber: Yup.string().label('Phone Number'),
})

const AddCustomerModal = ({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean
  onClose: () => void
  onSubmit: (customer: CreateOrderFormCustomer) => void
  title?: string
}) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [addCustomer, { loading: addInProgress }] = useAddCustomerMutation({
    client: useGqlClient(),
  })

  const handleSubmit = async (values: CustomerForm) => {
    if (!values || addInProgress) {
      return
    }

    const result = await addCustomer({
      variables: {
        companyName: values.companyName,
        accountNumber: values.accountNumber,
        address: values.address ? AddressModule.addressToAddressInput(values.address) : null,
        phoneNumber: values.companyPhoneNumber,
        contactName: values.contactName,
        email: values.contactEmail,
        contactPhoneNumber: values.contactPhoneNumber,
        message: '',
      },
      // The server names the missing address fields (GEA-6577). Nothing was
      // written, so the seller can correct the form and submit again.
    }).catch((err: unknown) => {
      msgsMgr.add(MutationErrorM.mutationErrorMessage(err), 'negative')
      return null
    })

    if (!result) return

    const customer = result.data?.addCustomer

    if (onSubmit && customer) {
      onClose()
      onSubmit(customer as CreateOrderFormCustomer)
    }
  }

  const form = useForm<CustomerForm>({
    defaultValues: {
      companyName: '',
      companyPhoneNumber: '',
      address: null,
      contactName: '',
      contactEmail: '',
      contactPhoneNumber: '',
    },
    resolver: yupResolver(validationSchema),
  })

  const address = useWatch({
    name: 'address',
    control: form.control,
    defaultValue: null,
  })

  return (
    <Modal onClose={onClose} open={open} size={ModalSize.SM}>
      <div className="relative p-6">
        <CloseModalButton onClick={onClose} className="absolute top-3 right-3" />
        <h2 className="text-2xl font-medium pb-4">Add New Customer</h2>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Controller
              control={form.control}
              name="companyName"
              render={({ field, fieldState }) => (
                <Field label="Company Name" error={fieldState.error?.message}>
                  <GoogleBusinessTypeahead
                    value={field.value}
                    onChange={field.onChange}
                    onDetailsFound={(details) => {
                      if (details.address) {
                        form.setValue('address', details.address, { shouldValidate: true })
                      }
                      if (details.name) {
                        form.setValue('companyName', details.name, { shouldValidate: true })
                      }
                      if (details.phoneNumber) {
                        form.setValue('companyPhoneNumber', details.phoneNumber, {
                          shouldValidate: true,
                        })
                        form.setValue('contactPhoneNumber', details.phoneNumber, {
                          shouldValidate: true,
                        })
                      }
                    }}
                  />
                </Field>
              )}
            />

            <div className="grid grid-cols-2 gap-2">
              <Field label="Account Number" error={form.formState.errors.accountNumber?.message}>
                <TextInput {...form.register('accountNumber')} />
              </Field>

              <Controller
                control={form.control}
                name="companyPhoneNumber"
                render={({ field, fieldState }) => (
                  <Field error={fieldState.error?.message}>
                    <PhoneInput value={field.value} onChange={field.onChange} />
                  </Field>
                )}
              />
            </div>

            <Controller
              control={form.control}
              name="address"
              render={({ field }) => (
                <Field error={form.formState.errors.address?.message}>
                  <AddressInput
                    address={address}
                    hideCompany
                    hideFirstLastName
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          </div>

          <hr />

          <div className="space-y-2">
            <h3 className="pb-2">Customer Contact Information</h3>
            <Field label="Full Name" error={form.formState.errors.contactName?.message}>
              <TextInput {...form.register('contactName')} />
            </Field>

            <div className="grid grid-cols-2 gap-x-2">
              <Controller
                control={form.control}
                name="contactPhoneNumber"
                render={({ field, fieldState }) => (
                  <Field error={fieldState.error?.message}>
                    <PhoneInput value={field.value} onChange={field.onChange} />
                  </Field>
                )}
              />

              <Field label="Email Address" error={form.formState.errors.contactEmail?.message}>
                <TextInput {...form.register('contactEmail')} />
              </Field>
            </div>
          </div>

          <Action.P
            type="submit"
            className="w-full font-medium"
            performing={addInProgress}
            disabled={addInProgress}
          >
            Confirm
          </Action.P>
        </form>
      </div>
    </Modal>
  )
}

export default AddCustomerModal
